/**
 * Main app entry point
 * Bootstraps auth, router, store, and renders the app shell
 */

import { LitElement, html } from 'lit';
import { auth } from './services/auth.js';
import { store } from './store.js';
import { router } from './router.js';
import { showLoading, hideLoading } from './components/loading-spinner.js';
import { showError } from './components/toast-notification.js';

// Shared components
import './components/app-shell.js';
import './components/toast-notification.js';
import './components/loading-spinner.js';
import './components/confirm-dialog.js';

// Views
import './views/auth-screen.js';
import './views/home-view.js';
import './views/recipes-view.js';
import './views/recipe-detail-view.js';
import './views/recipe-form-view.js';
import './views/planner-view.js';
import './views/inventory-view.js';
import './views/shopping-view.js';
import './views/budget-view.js';
import './views/settings-view.js';

// Styles
import './styles/global.css';

class MealPlannerApp extends LitElement {
  static properties = {
    _authenticated: { state: true },
    _loading: { state: true },
    _currentRoute: { state: true },
    _routeParams: { state: true },
  };

  createRenderRoot() { return this; }

  constructor() {
    super();
    this._authenticated = false;
    this._loading = true;
    this._currentRoute = '/';
    this._routeParams = {};
  }

  connectedCallback() {
    super.connectedCallback();

    // Auth events
    this._onAuthSuccess = async () => {
      try {
        showLoading();
        await store.loadAll();
        this._authenticated = true;
        if (!window.location.hash || window.location.hash === '#/') {
          window.location.hash = '#/';
        }
        router.handleRoute();
      } catch (error) {
        console.error('Error loading data:', error);
        showError('Failed to load data from Firestore.');
      } finally {
        hideLoading();
      }
    };

    this._onAuthSignout = () => {
      this._authenticated = false;
      this._currentRoute = '/';
    };

    this._onAuthError = (e) => {
      showError('Authentication failed: ' + e.detail.error);
    };

    window.addEventListener('auth-success', this._onAuthSuccess);
    window.addEventListener('auth-signout', this._onAuthSignout);
    window.addEventListener('auth-error', this._onAuthError);

    // Route events
    this._onRouteChange = (e) => {
      this._currentRoute = e.detail.path;
      this._routeParams = e.detail.params;
    };
    window.addEventListener('route-change', this._onRouteChange);

    // Register routes and init
    router.register({
      '/': 'home-view',
      '/recipes': 'recipes-view',
      '/recipes/new': 'recipe-form-view',
      '/recipes/:id': 'recipe-detail-view',
      '/recipes/:id/edit': 'recipe-form-view',
      '/planner': 'planner-view',
      '/inventory': 'inventory-view',
      '/shopping': 'shopping-view',
      '/budget': 'budget-view',
      '/settings': 'settings-view',
    });

    this._initAuth();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('auth-success', this._onAuthSuccess);
    window.removeEventListener('auth-signout', this._onAuthSignout);
    window.removeEventListener('auth-error', this._onAuthError);
    window.removeEventListener('route-change', this._onRouteChange);
    router.stop();
  }

  async _initAuth() {
    try {
      showLoading();
      const isSignedIn = await auth.init();

      if (isSignedIn) {
        await store.loadAll();
        this._authenticated = true;
        router.start();
      } else {
        this._authenticated = false;
        router.start();
      }
    } catch (error) {
      console.error('Init error:', error);
      showError('Failed to initialize: ' + error.message);
    } finally {
      this._loading = false;
      hideLoading();
    }
  }

  _renderCurrentView() {
    const path = this._currentRoute;
    const params = this._routeParams;

    if (path === '/') return html`<home-view></home-view>`;
    if (path === '/recipes') return html`<recipes-view></recipes-view>`;
    if (path === '/recipes/new') return html`<recipe-form-view></recipe-form-view>`;
    if (path.match(/^\/recipes\/[^/]+\/edit$/)) return html`<recipe-form-view .recipeId=${params.id}></recipe-form-view>`;
    if (path.match(/^\/recipes\/[^/]+$/)) return html`<recipe-detail-view .recipeId=${params.id}></recipe-detail-view>`;
    if (path === '/planner') return html`<planner-view></planner-view>`;
    if (path === '/inventory') return html`<inventory-view></inventory-view>`;
    if (path === '/shopping') return html`<shopping-view></shopping-view>`;
    if (path === '/budget') return html`<budget-view></budget-view>`;
    if (path === '/settings') return html`<settings-view></settings-view>`;

    return html`<home-view></home-view>`;
  }

  render() {
    if (this._loading) {
      return html``;
    }

    if (!this._authenticated) {
      return html`
        <auth-screen></auth-screen>
        <toast-notification></toast-notification>
        <loading-spinner></loading-spinner>
      `;
    }

    return html`
      <app-shell current-path=${this._currentRoute}>
        ${this._renderCurrentView()}
      </app-shell>
      <toast-notification></toast-notification>
      <loading-spinner></loading-spinner>
      <confirm-dialog></confirm-dialog>
    `;
  }
}

customElements.define('meal-planner-app', MealPlannerApp);
